// Legal document content for Thortek Performance.
// NOTE: These are plain-language templates, not legal advice. Have an attorney
// review before relying on them. Replace every [BRACKETED] placeholder.

export const LEGAL_UPDATED = "June 2026";

export const LEGAL_ORDER = ["terms", "privacy", "disclaimer", "refund"];

export const LEGAL_DOCS = {
  terms: {
    label: "Terms of Service",
    title: "Terms of Service & License Agreement",
    intro:
      "These Terms of Service (\u201cTerms\u201d) govern your purchase and use of the Thortek Performance application and website (the \u201cApp\u201d), operated by Jeff Hernandez (\u201cThortek Performance,\u201d \u201cwe,\u201d \u201cus\u201d). By purchasing, accessing, or using the App, you agree to these Terms. If you do not agree, do not use the App.",
    sections: [
      {
        h: "1. License grant",
        p: "Upon payment of the one-time fee, we grant you a personal, non-exclusive, non-transferable, revocable license to use the App for your own vehicle-alignment purposes. \u201cLifetime access\u201d means access for the operational lifetime of the App and service; it does not guarantee any specific duration, feature, or that the service will be offered indefinitely.",
      },
      {
        h: "2. What you may not do",
        p: "You may not resell, sublicense, rent, share, or distribute your license key; reverse-engineer, decompile, or copy the App; use it to build a competing product; or use it for any unlawful purpose. Your license key is confidential \u2014 sharing it may result in revocation without refund.",
      },
      {
        h: "3. Not a professional service",
        p: "The App provides measurement aids and reference specifications. It is a tool, not a substitute for a professional wheel-alignment service or the judgment of a qualified technician. See our Safety & Liability Disclaimer, which is incorporated into these Terms.",
      },
      {
        h: "4. Intellectual property",
        p: "The App, its branding (\u201cThortek Performance\u201d), logos, content, and software are owned by Jeff Hernandez and protected by intellectual-property laws. These Terms grant you a license to use the App, not any ownership rights.",
      },
      {
        h: "5. Payments",
        p: "Payments are processed by Stripe. Prices are shown at checkout and may change for future purchases. You are responsible for any applicable taxes. Refunds are governed by our Refund Policy.",
      },
      {
        h: "6. No warranty",
        p: "The App is provided \u201cas is\u201d and \u201cas available,\u201d without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy, or non-infringement. We do not warrant that measurements will be accurate or that the App will be uninterrupted or error-free.",
      },
      {
        h: "7. Limitation of liability",
        p: "To the maximum extent permitted by law, Thortek Performance and Jeff Hernandez will not be liable for any indirect, incidental, special, or consequential damages, or for any vehicle damage, personal injury, or loss arising from your use of the App. Our total liability for any claim will not exceed the amount you paid for the App.",
      },
      {
        h: "8. Termination",
        p: "We may suspend or revoke your license if you breach these Terms (for example, sharing your key or misusing the App). We may discontinue the App or any feature at any time.",
      },
      {
        h: "9. Governing law",
        p: "These Terms are governed by the laws of [STATE/JURISDICTION], without regard to conflict-of-law rules. Any disputes will be handled in the courts located in [COUNTY, STATE].",
      },
      {
        h: "10. Contact",
        p: "Questions about these Terms? Contact us at contact@thortekperformance.com.",
      },
    ],
  },

  privacy: {
    label: "Privacy Policy",
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how the Thortek Performance App handles your information. We built the App to require no account and to keep your data on your device wherever possible.",
    sections: [
      {
        h: "1. No account required",
        p: "You do not create an account or provide a username/password to use the App. Access is unlocked with a one-time license key stored locally in your browser.",
      },
      {
        h: "2. Data stored on your device",
        p: "Your vehicle selections, saved sessions, measurements, and license key are stored locally on your device (browser local storage). We do not need or collect this to operate the App, and it stays on your device unless you choose to share a report.",
      },
      {
        h: "3. Camera and motion sensors",
        p: "Features like Visual Scan, AR Overlay, and Tire-Wear use your device camera and motion sensors with your permission. For AI-assisted features, the specific image you capture is sent to our server solely to generate your result and is not used to identify you. We do not continuously record or store your camera feed.",
      },
      {
        h: "4. Payment information",
        p: "Payments are handled by Stripe. We do not see or store your full card details. Stripe processes your payment and may collect billing information under its own privacy policy (stripe.com/privacy). We retain a record of the transaction and the license key issued.",
      },
      {
        h: "5. Shareable reports",
        p: "If you create a shareable before/after report, the information in that report is stored on our server so it can be viewed via its link. Anyone with the link can view it. Do not include sensitive personal information in reports.",
      },
      {
        h: "6. What we don't do",
        p: "We do not sell your personal information. We do not use it for third-party advertising.",
      },
      {
        h: "7. Children",
        p: "The App is intended for adults and is not directed to children under 13 (or the minimum age in your jurisdiction).",
      },
      {
        h: "8. Your choices & contact",
        p: "You can clear local data by clearing your browser storage. To request deletion of a shared report or ask a privacy question, contact contact@thortekperformance.com.",
      },
    ],
  },

  disclaimer: {
    label: "Safety & Liability Disclaimer",
    title: "Safety & Liability Disclaimer",
    intro:
      "Please read this carefully. Wheel alignment affects how your vehicle steers, brakes, and handles. Improper alignment can be dangerous.",
    sections: [
      {
        h: "1. The App is a measurement aid, not a professional alignment",
        p: "Thortek Performance helps you estimate and reference alignment angles (camber, toe, caster and related measurements). It is NOT a substitute for a professional wheel-alignment performed on calibrated equipment by a qualified technician.",
      },
      {
        h: "2. Accuracy depends on many factors",
        p: "Results depend on your device's sensors, camera, how level your surface is, how you position the device, tire condition, and other factors. Measurements may be inaccurate. Always cross-check against the vehicle manufacturer's official specifications and service procedures.",
      },
      {
        h: "3. You assume the risk",
        p: "You are solely responsible for any adjustments you make to your vehicle. If you are not confident, stop and consult a qualified professional. Incorrect alignment or improper work on steering and suspension components can cause loss of control, crashes, tire failure, injury, or death.",
      },
      {
        h: "4. Verify before driving",
        p: "After any alignment or suspension work, have the vehicle inspected by a qualified professional and verify it is safe before driving on public roads.",
      },
      {
        h: "5. No liability",
        p: "To the fullest extent permitted by law, Thortek Performance and Jeff Hernandez disclaim all liability for any damage to vehicles or property, personal injury, death, or any other loss resulting from use of or reliance on the App or its measurements. You use the App entirely at your own risk.",
      },
      {
        h: "6. Not affiliated with manufacturers",
        p: "Vehicle names, specifications, and VIN data are used for identification and reference only. Thortek Performance is not affiliated with, endorsed by, or sponsored by any vehicle manufacturer.",
      },
    ],
  },

  refund: {
    label: "Refund Policy",
    title: "Refund Policy",
    intro:
      "Thortek Performance is a one-time digital purchase that unlocks lifetime access immediately. Because access is granted instantly, please review this policy before buying.",
    sections: [
      {
        h: "1. Digital product",
        p: "Your purchase delivers a license key granting immediate access to digital software. By completing checkout you acknowledge access begins right away.",
      },
      {
        h: "2. Satisfaction / technical-issue refunds",
        p: "If the App does not work for you due to a technical problem we cannot resolve, you may request a refund within [14] days of purchase. We may ask for details about the issue and your device so we can try to help first.",
      },
      {
        h: "3. How to request",
        p: "Email contact@thortekperformance.com with the email address used at checkout and your Stripe receipt or the last 4 digits of the card used. Approved refunds are issued to the original payment method via Stripe and may take several business days to appear.",
      },
      {
        h: "4. When refunds may be declined",
        p: "We may decline refunds requested after [14] days, for license keys that have been shared or misused, or where there is evidence of abuse of this policy.",
      },
      {
        h: "5. Your statutory rights",
        p: "Nothing in this policy limits any non-waivable consumer rights you may have under the laws of your jurisdiction.",
      },
      {
        h: "6. Contact",
        p: "Refund questions? Contact contact@thortekperformance.com.",
      },
    ],
  },
};
